import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useHotelAdminStore } from '../store'
import { apiClient } from '../services/apiClient'
import { extractErrorMessage } from '../utils/errorHandler'
import { v4 as uuidv4 } from 'uuid'

export default function AdminSubscriptionSelectPage() {
  const navigate = useNavigate()
  const admin = useHotelAdminStore((state) => state.admin)
  const getAccessToken = useHotelAdminStore((state) => state.getAccessToken)
  
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState('')
  const [success, setSuccess] = useState('')

  // Единственный тариф: годовая подписка 10,000 рублей
  const plan = {
    id: 'admin_year',
    name: 'Годовая подписка',
    price: '10 000 ₽',
    period: 'в год',
    description: 'Полный год доступа к сервису для администратора отеля',
    features: [
      'Безлимит генераций документов для гостей',
      'Поддержка на протяжении года',
      'Сохранение истории документов',
      'Возможность управления шаблонами',
      'Доступ ко всем функциям платформы'
    ],
  }

  const handleSelectPlan = async () => {
    try {
      setError('')
      setSuccess('')
      setIsLoading(true)

      // 🔑 Получаем токен из localStorage вместо admin.access_token
      const accessToken = getAccessToken()
      
      if (!accessToken) {
        setError('Ошибка авторизации. Пожалуйста, перезагрузитесь.')
        setIsLoading(false)
        return
      }

      // Генерируем идемпотентный ключ для создания счета
      const idempotencyKey = uuidv4()

      console.log('Creating invoice for tariff:', plan.id)
      console.log('Access token present:', !!accessToken)

      // Создаём счет в YooKassa через API
      const response = await apiClient.post(
        '/payments/create-invoice',
        {
          tariff_id: plan.id,
          idempotency_key: idempotencyKey,
        }
      )

      console.log('🔵 Invoice response received:', response)
      console.log('Response status:', response.status)
      console.log('Response confirmation_url:', response.confirmation_url)

      if (response.status !== 'ok' || !response.confirmation_url) {
        throw new Error(`Ошибка при создании счета: отсутствует ссылка оплаты. Response: ${JSON.stringify(response)}`)
      }

      console.log('Invoice created:', response.invoice_id)
      setSuccess('✅ Счет создан. Перенаправляю на Youmoney...')

      // Редирект на страницу оплаты Youmoney
      setTimeout(() => {
        window.location.href = response.confirmation_url
      }, 1000)
      
    } catch (err: any) {
      console.error('Plan selection error:', err)
      const errorMsg = extractErrorMessage(err) || 'Ошибка при создании счета'
      setError(errorMsg)
      setIsLoading(false)
    }
  }

  if (!admin?.email) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gradient-to-br from-indigo-600 to-blue-600 p-4">
        <div className="w-full max-w-md bg-white rounded-lg shadow-lg p-8 text-center">
          <h2 className="text-2xl font-bold text-gray-800 mb-4">Ошибка</h2>
          <p className="text-gray-600 mb-6">Данные админа не найдены. Пожалуйста, зарегистрируйтесь заново.</p>
          <button
            onClick={() => navigate('/admin-login')}
            className="w-full bg-indigo-600 hover:bg-indigo-700 text-white font-semibold py-2 rounded-lg"
          >
            Вернуться на вход
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-600 to-blue-600 p-4 py-12">
      <div className="max-w-4xl mx-auto">
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold text-white mb-3">Активируйте подписку</h1>
          <p className="text-xl text-indigo-100">
            Полный год доступа к системе HotelCheck
          </p>
          <p className="text-sm text-indigo-200 mt-2">
            Администратор: <span className="font-semibold">{admin.full_name} ({admin.email})</span>
          </p>
          <p className="text-sm text-indigo-200">
            Отель: <span className="font-semibold">{admin.hotel_name}</span>
          </p>
        </div>

        {error && (
          <div className="max-w-2xl mx-auto mb-6 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-center">
            ⚠️ {error}
          </div>
        )}

        {success && (
          <div className="max-w-2xl mx-auto mb-6 bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg text-center">
            {success}
          </div>
        )}

        {/* Основная карточка тарифа */}
        <div className="max-w-2xl mx-auto">
          <div className="bg-white rounded-lg shadow-2xl overflow-hidden">
            {/* Заголовок */}
            <div className="bg-gradient-to-r from-indigo-600 to-indigo-700 text-white p-8 text-center">
              <div className="text-5xl font-bold mb-2">{plan.price}</div>
              <div className="text-2xl font-semibold">{plan.name}</div>
              <div className="text-indigo-100 mt-2">{plan.period}</div>
            </div>

            {/* Описание */}
            <div className="p-8 border-b border-gray-200">
              <p className="text-gray-600 text-center text-lg">{plan.description}</p>
            </div>

            {/* Функции */}
            <div className="p-8">
              <h3 className="text-lg font-bold text-gray-800 mb-6">Что входит в подписку:</h3>
              <ul className="space-y-4 mb-8">
                {plan.features.map((feature, idx) => (
                  <li key={idx} className="flex items-start text-gray-700">
                    <span className="text-green-500 mr-4 font-bold text-xl">✓</span>
                    <span className="text-base">{feature}</span>
                  </li>
                ))}
              </ul>

              {/* Кнопка оплаты */}
              <button
                onClick={handleSelectPlan}
                disabled={isLoading}
                className={`w-full py-4 rounded-lg font-semibold text-lg transition transform ${
                  isLoading
                    ? 'opacity-50 cursor-not-allowed bg-gray-400'
                    : 'bg-gradient-to-r from-indigo-600 to-indigo-700 hover:from-indigo-700 hover:to-indigo-800 text-white hover:shadow-lg'
                }`}
              >
                {isLoading ? '⏳ Создаю счет...' : '💳 Перейти к оплате'}
              </button>

              <p className="text-center text-sm text-gray-500 mt-4">
                Платеж осуществляется через Youmoney
              </p>
            </div>

            {/* Гарантия */}
            <div className="bg-blue-50 px-8 py-6 border-t border-gray-200">
              <p className="text-center text-sm text-gray-600">
                <span className="font-semibold">🔒 Безопасность:</span> Ваши платежные данные защищены стандартом PCI DSS.<br/>
                <span className="font-semibold">❌ Без скрытых платежей:</span> Только один платеж в год, никаких дополнительных комиссий.
              </p>
            </div>
          </div>
        </div>

        {/* FAQ */}
        <div className="mt-12 max-w-2xl mx-auto">
          <h3 className="text-2xl font-bold text-white mb-6 text-center">Часто задаваемые вопросы</h3>
          
          <div className="space-y-4">
            <div className="bg-white/10 backdrop-blur rounded-lg p-6 text-white">
              <h4 className="font-bold mb-2">❓ Какой период тестирования?</h4>
              <p className="text-indigo-100">Нет периода тестирования. Подписка активируется сразу после оплаты.</p>
            </div>

            <div className="bg-white/10 backdrop-blur rounded-lg p-6 text-white">
              <h4 className="font-bold mb-2">❓ Можно ли отменить платеж?</h4>
              <p className="text-indigo-100">Да, вы можете запросить возврат в течение 14 дней. Свяжитесь с нашей поддержкой support@hotelcheck.ru</p>
            </div>

            <div className="bg-white/10 backdrop-blur rounded-lg p-6 text-white">
              <h4 className="font-bold mb-2">❓ Кто оплачивает подписку?</h4>
              <p className="text-indigo-100">Только администратор отеля оплачивает подписку. Гости ничего не платят и могут генерировать документы только если у админа активна подписка.</p>
            </div>

            <div className="bg-white/10 backdrop-blur rounded-lg p-6 text-white">
              <h4 className="font-bold mb-2">❓ Как гость генерирует документы?</h4>
              <p className="text-indigo-100">Гость выбирает отель, загружает паспорт, а система автоматически генерирует документы для регистрации. Если подписка админа активна - документы генерируются, если нет - гость видит сообщение об ошибке.</p>
            </div>
          </div>
        </div>

        {/* Кнопка возврата */}
        <div className="text-center mt-12">
          <button
            onClick={() => navigate('/admin-dashboard')}
            className="text-white hover:text-indigo-100 underline"
          >
            ← Вернуться на панель администратора
          </button>
        </div>
      </div>
    </div>
  )
}
